import type { TemplateDraft, TemplateRow } from '../message-templates'

/**
 * The wording of each push notification — FRONTEND.md §9.206, BACKEND.md §10A.83.
 *
 * The page above this decided *which channels* carry an event and never *what any of them say*: the
 * seven push sentences were Java string literals while every SMS beside them came out of
 * `sms_settings`.
 *
 * **The rules moved to `../message-templates` in §9.322** and only the payload reader is left here.
 * That entry built the same editor for e-mail on the same payload shape, and keeping two copies of
 * *what counts as an override* is how one of them ends up with a guard the other lacks. The
 * behaviour is unchanged - `isOverridden`, `effectiveText`, `previewOf`, `fillPlaceholders`,
 * `unknownPlaceholders` and `placeholderHint` are re-exported from here so no caller had to move.
 */

export {
  effectiveText,
  fillPlaceholders,
  isOverridden,
  placeholderHint,
  PREVIEW_VALUES,
  previewOf,
  unknownPlaceholders,
} from '../message-templates'

/** The shared row, under this channel's own name - see `../message-templates`. */
export type PushMessage = TemplateRow

/**
 * `GET /notifications/push-messages`, read by name.
 *
 * `title` is mapped onto `first` so the shared rules apply unchanged; the screen labels it *Nadpis*
 * and `payloadOf` puts the server's own name back.
 */
export function pushMessagesOf(payload: unknown): PushMessage[] {
  if (!Array.isArray(payload)) return []
  return payload.map((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>
    return {
      key: typeof row.key === 'string' ? row.key : '',
      label: typeof row.label === 'string' ? row.label : '',
      desc: typeof row.desc === 'string' ? row.desc : '',
      defaultFirst: typeof row.defaultTitle === 'string' ? row.defaultTitle : '',
      defaultBody: typeof row.defaultBody === 'string' ? row.defaultBody : '',
      first: typeof row.title === 'string' ? row.title : null,
      body: typeof row.body === 'string' ? row.body : null,
      updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : null,
      placeholders: Array.isArray(row.placeholders)
        ? row.placeholders.filter((name): name is string => typeof name === 'string')
        : [],
    }
  })
}

/** Only the two keys the endpoint accepts, so a save cannot carry a label or a default back. */
export function payloadOf(draft: TemplateDraft): Record<string, unknown> {
  return { title: draft.first.trim(), body: draft.body.trim() }
}
